/**
 * VitalsForm.jsx
 * CCH Care — Phase 4
 * Last Modified: 2026-06-08
 *
 * Follows HygieneForm patterns exactly:
 *   - Inline styles using COLORS from ../../config/constants
 *   - Renders ADLSectionCard as root element (not a child of it in the page)
 *   - Props match the pattern: adlRecordId, initialData, sectionStatus,
 *     amendments, onSign, disabled
 *   - No external CSS — all styling is inline
 *
 * Append-only readings (like fluid intake / repositioning), not a single form.
 * Each shift can have multiple timed readings.
 *
 * N/A wiring (2026-06-08):
 * - Consumes the care-plan isNA flag. When the section is flagged
 *   not-applicable the card renders locked with the N/A banner and the
 *   readings list / add-reading flow are hidden. loadVitals short-circuits
 *   when N/A to skip a pointless fetch. Vitals has no nothing-to-report
 *   attestation, so ADLSectionNA is rendered only in the N/A case.
 *
 * Self-managed amendment fix (2026-05-19):
 * - VitalsForm passes `selfManagedAmendment` to ADLSectionCard so the
 *   generic footer "Amend" button is NOT rendered for Vitals. Vitals has
 *   its own in-section "+ Add Amendment" flow (amendVitals -> vitals.php)
 *   and does not use the shared ADLAmendmentModal. Because VitalsForm
 *   passes `handleSignClick` as `onSign`, the generic footer Amend was
 *   calling the SIGN handler with an 'amend_vitals' argument it ignores —
 *   opening the "Sign Vitals Section" modal on an already-signed section.
 *   Confirm & Sign then 409'd ("Vitals section is already signed"). The
 *   correct path is the in-section "+ Add Amendment" button below, which
 *   becomes available once the section is signed.
 *
 * Changes (2026-05-08) — Dark mode legibility fix:
 * - Replaced hardcoded amber-cream surfaces (#fff8f0, #fff3cd, #f39c12,
 *   #d68910, #856404) with COLORS.warningLight / COLORS.warning tokens.
 *   Out-of-range vital input values were unreadable in dark mode because
 *   the cream backgrounds didn't theme — light text on cream was invisible.
 *   Themed warningLight (cream in light mode, dark brown in dark mode) now
 *   pairs correctly with COLORS.text in both modes.
 * - Error banner: '#fdf0f0' / '#e74c3c' / '#c0392b' → COLORS.dangerLight /
 *   COLORS.danger.
 * - Modal backdrop and shadow rgba values left inline (deferred to global
 *   modalBackdrop/modalShadow token decision).
 */

import { useState, useEffect, useCallback } from 'react';
import { COLORS } from '../../config/constants';
import { getVitals, saveVitals, updateVitals, amendVitals, signVitals } from '../../api/adl';
import ADLSectionCard from '../../components/ui/ADLSectionCard';
import ADLAmendmentBanner from '../../components/ui/ADLAmendmentBanner';
import ADLSectionNA from '../../components/ui/ADLSectionNA';

import { formatTime } from '../../utils/formatters';
// ---------------------------------------------------------------------------
// Normal ranges — mirrors backend vitals_is_abnormal() thresholds
// ---------------------------------------------------------------------------
const RANGES = {
  systolic:         { min: 90,   max: 140,  unit: 'mmHg', label: 'Systolic BP'  },
  diastolic:        { min: 60,   max: 90,   unit: 'mmHg', label: 'Diastolic BP' },
  pulse:            { min: 60,   max: 100,  unit: 'bpm',  label: 'Pulse'        },
  temperature:      { min: 97.0, max: 99.5, unit: '°F',   label: 'Temperature'  },
  o2_saturation:    { min: 95,   max: 100,  unit: '%',    label: 'O₂ Sat'       },
  respiratory_rate: { min: 12,   max: 20,   unit: '/min', label: 'Resp Rate'    },
};

function isAbnormal(field, value) {
  if (value === '' || value === null || value === undefined) return false;
  const v = parseFloat(value);
  const r = RANGES[field];
  return r ? (v < r.min || v > r.max) : false;
}

const emptyForm = () => {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000)
    .toISOString().slice(0, 16);
  return {
    systolic: '', diastolic: '', pulse: '', temperature: '',
    o2_saturation: '', respiratory_rate: '',
    recorded_at: local,
    notes: '',
  };
};

// ---------------------------------------------------------------------------
// Single reading display row
// ---------------------------------------------------------------------------
function ReadingRow({ reading, onEdit, isSigned }) {
  const time = formatTime(reading.recorded_at);

  const vitals = [
    reading.systolic || reading.diastolic
      ? { key: 'bp', label: 'BP', value: `${reading.systolic ?? '—'}/${reading.diastolic ?? '—'}`, unit: 'mmHg',
          abnormal: isAbnormal('systolic', reading.systolic) || isAbnormal('diastolic', reading.diastolic) }
      : null,
    reading.pulse            != null ? { key: 'pulse', label: 'Pulse',     value: reading.pulse,            unit: 'bpm',  abnormal: isAbnormal('pulse', reading.pulse) }            : null,
    reading.temperature      != null ? { key: 'temp',  label: 'Temp',      value: parseFloat(reading.temperature).toFixed(1), unit: '°F', abnormal: isAbnormal('temperature', reading.temperature) } : null,
    reading.o2_saturation    != null ? { key: 'o2',    label: 'O₂ Sat',    value: reading.o2_saturation,    unit: '%',    abnormal: isAbnormal('o2_saturation', reading.o2_saturation) }    : null,
    reading.respiratory_rate != null ? { key: 'rr',    label: 'Resp Rate', value: reading.respiratory_rate, unit: '/min', abnormal: isAbnormal('respiratory_rate', reading.respiratory_rate) } : null,
  ].filter(Boolean);

  return (
    <div style={{
      background: !!reading.has_abnormal ? COLORS.warningLight : COLORS.surface,
      border: `1px solid ${!!reading.has_abnormal ? COLORS.warning : COLORS.border}`,
      borderRadius: 8, padding: '10px 14px', marginBottom: 8,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: COLORS.textMuted }}>{time}</span>
          {!!reading.has_abnormal && (
            <span style={{
              fontSize: 10, fontWeight: 700, color: COLORS.warning,
              background: COLORS.warningLight, padding: '2px 6px', borderRadius: 4,
            }}>
              ⚠ ABNORMAL
            </span>
          )}
          {reading.section_status === 'signed' && reading.created_at > reading.recorded_at && (
            <span style={{
              fontSize: 10, fontWeight: 700, color: COLORS.textMuted,
              background: COLORS.bg, border: `1px solid ${COLORS.border}`,
              padding: '2px 6px', borderRadius: 4,
            }}>
              AMENDED
            </span>
          )}
        </div>
        {!isSigned && onEdit && (
          <button
            onClick={() => onEdit(reading)}
            style={{
              background: 'none', border: 'none', fontSize: 11,
              color: COLORS.primary, cursor: 'pointer', padding: '2px 6px',
            }}
          >
            Edit
          </button>
        )}
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {vitals.map(v => (
          <div key={v.key} style={{
            background: v.abnormal ? COLORS.warningLight : COLORS.bg,
            border: `1px solid ${v.abnormal ? COLORS.warning : COLORS.border}`,
            borderRadius: 6, padding: '4px 10px', fontSize: 12,
          }}>
            <span style={{ color: COLORS.textMuted, marginRight: 4 }}>{v.label}</span>
            <span style={{ fontWeight: 700, color: v.abnormal ? COLORS.warning : COLORS.text }}>
              {v.value}
            </span>
            <span style={{ color: COLORS.textMuted, fontSize: 10, marginLeft: 2 }}>{v.unit}</span>
            {v.abnormal && <span style={{ marginLeft: 4 }}>⚠</span>}
          </div>
        ))}
      </div>

      {reading.notes && (
        <p style={{ fontSize: 11, color: COLORS.textMuted, margin: '6px 0 0', fontStyle: 'italic' }}>
          {reading.notes}
        </p>
      )}

      <p style={{ fontSize: 11, color: COLORS.textMuted, margin: '4px 0 0' }}>
        — {reading.recorded_by_name}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Vital input field with out-of-range highlight
// ---------------------------------------------------------------------------
function VitalInput({ name, value, onChange, disabled }) {
  const r = RANGES[name];
  const abnormal = isAbnormal(name, value);
  const inputId = `vital-${name}`;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <label htmlFor={inputId} style={{ fontSize: 11, fontWeight: 600, color: abnormal ? COLORS.warning : COLORS.textMuted }}>
        {r.label}
        <span style={{ fontWeight: 400, marginLeft: 4 }}>({r.min}–{r.max} {r.unit})</span>
      </label>
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        <input
          id={inputId}
          type="number"
          value={value}
          onChange={e => onChange(name, e.target.value)}
          disabled={disabled}
          step={name === 'temperature' ? '0.1' : '1'}
          min={0}
          placeholder="—"
          style={{
            width: '100%', padding: '7px 36px 7px 10px', borderRadius: 6,
            border: `1px solid ${abnormal ? COLORS.warning : COLORS.border}`,
            fontSize: 13, fontWeight: 600, color: COLORS.text,
            background: abnormal ? COLORS.warningLight : COLORS.surface,
            boxSizing: 'border-box',
          }}
        />
        <span style={{
          position: 'absolute', right: 8, fontSize: 11,
          color: abnormal ? COLORS.warning : COLORS.textMuted, pointerEvents: 'none',
        }}>
          {abnormal ? '⚠' : r.unit}
        </span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function VitalsForm({
  adlRecordId,
  initialData = [],
  sectionStatus = 'draft',
  amendments = [],
  onSign,
  disabled = false,
  onDocumentedCountChange,
  // Phase 5d N/A wiring
  isNA = false,
  naReason = '',
}) {
  const [readings, setReadings]       = useState(initialData);
  const [cosignature, setCosignature] = useState(null);
  const [form, setForm]               = useState(emptyForm());
  const [editingId, setEditingId]     = useState(null);
  const [showForm, setShowForm]       = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading]         = useState(!initialData.length);
  const [saving, setSaving]           = useState(false);
  const [signing, setSigning]         = useState(false);
  const [error, setError]             = useState(null);

  const isSigned   = sectionStatus === 'signed';
  const isCosigned = !!cosignature;
  const isLocked   = disabled || isCosigned;
  const abnormalReadings = readings.filter(r => r.has_abnormal);

  // Notify parent of reading count
  useEffect(() => {
    onDocumentedCountChange?.(readings.length);
  }, [readings.length, onDocumentedCountChange]);

  // ---------------------------------------------------------------------------
  // Load
  // ---------------------------------------------------------------------------
  const loadVitals = useCallback(async () => {
    if (!adlRecordId || isNA) return;
    setError(null);
    try {
      const data = await getVitals(adlRecordId);
      if (!data.success) throw new Error(data.error || 'Failed to load vitals');
      setReadings(data.readings || []);
      setCosignature(data.cosignature || null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [adlRecordId, isNA]);

  useEffect(() => { loadVitals(); }, [loadVitals]);

  // ---------------------------------------------------------------------------
  // Form helpers
  // ---------------------------------------------------------------------------
  function handleFieldChange(name, value) {
    setForm(f => ({ ...f, [name]: value }));
  }

  function formHasValues() {
    return ['systolic','diastolic','pulse','temperature','o2_saturation','respiratory_rate']
      .some(f => form[f] !== '');
  }

  function startAdd() {
    setForm(emptyForm());
    setEditingId(null);
    setShowForm(true);
  }

  function startEdit(reading) {
    setForm({
      systolic:         reading.systolic         ?? '',
      diastolic:        reading.diastolic        ?? '',
      pulse:            reading.pulse            ?? '',
      temperature:      reading.temperature      ?? '',
      o2_saturation:    reading.o2_saturation    ?? '',
      respiratory_rate: reading.respiratory_rate ?? '',
      recorded_at:      reading.recorded_at
        ? new Date(reading.recorded_at).toISOString().slice(0,16)
        : new Date().toISOString().slice(0,16),
      notes: reading.notes ?? '',
    });
    setEditingId(reading.adl_vitals_id);
    setShowForm(true);
  }

  function cancelForm() {
    setShowForm(false);
    setEditingId(null);
    setForm(emptyForm());
    setError(null);
  }

  // ---------------------------------------------------------------------------
  // Save / Update / Amend
  // ---------------------------------------------------------------------------
  async function handleSave() {
    if (!formHasValues()) {
      setError('Enter at least one vital sign value.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const vitalsData = {
        ...Object.fromEntries(Object.entries(form).filter(([, v]) => v !== '')),
        recorded_at: form.recorded_at.replace('T', ' ') + ':00',
      };

      let data;
      if (editingId) {
        data = await updateVitals(editingId, vitalsData);
      } else if (isSigned) {
        data = await amendVitals(adlRecordId, vitalsData);
      } else {
        data = await saveVitals(adlRecordId, vitalsData);
      }

      if (!data.success) throw new Error(data.error || 'Save failed');
      await loadVitals();
      cancelForm();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Sign
  // ---------------------------------------------------------------------------
  function handleSignClick() {
    if (readings.length === 0) {
      setError('Add at least one reading before signing.');
      return;
    }
    if (abnormalReadings.length > 0) {
      setShowConfirm(true);
    } else {
      doSign();
    }
  }

  async function doSign() {
    setShowConfirm(false);
    setSigning(true);
    setError(null);
    try {
      const data = await signVitals(adlRecordId);
      if (!data.success) throw new Error(data.error || 'Sign failed');
      await loadVitals();
      onSign?.('vitals');
    } catch (err) {
      setError(err.message);
    } finally {
      setSigning(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Summary for ADLSectionCard collapsed header
  // ---------------------------------------------------------------------------
  const summaryText = readings.length === 0
    ? 'No readings yet'
    : `${readings.length} reading${readings.length > 1 ? 's' : ''}${
        abnormalReadings.length > 0 ? ` · ⚠ ${abnormalReadings.length} abnormal` : ''
      }`;

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <ADLSectionCard
      title="Vitals"
      icon="🩺"
      sectionKey="vitals"
      status={isNA ? 'na' : sectionStatus}
      summary={isNA ? undefined : summaryText}
      hasAmendments={amendments?.some(a => a.section === 'vitals')}
      onSign={isNA ? undefined : handleSignClick}
      disabled={disabled || isNA}
      selfManagedAmendment
    >
      <ADLAmendmentBanner amendments={amendments} section="vitals" />

      {/* N/A banner (care plan flag) — Vitals has no attestation path */}
      {isNA && (
        <ADLSectionNA
          section="vitals"
          isNA={isNA}
          naReason={naReason}
          supportsAttestation={false}
          isSectionSigned={sectionStatus === 'signed'}
        />
      )}

      {!isNA && (
        <>
          {/* Error banner */}
          {error && (
            <div style={{
              background: COLORS.dangerLight, border: `1px solid ${COLORS.danger}`,
              borderRadius: 6, padding: '8px 12px', marginBottom: 12,
              fontSize: 12, color: COLORS.danger,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              {error}
              <button
                onClick={() => setError(null)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: COLORS.danger, fontSize: 14 }}
              >
                ✕
              </button>
            </div>
          )}

          {/* Loading */}
          {loading ? (
            <p style={{ fontSize: 13, color: COLORS.textMuted, textAlign: 'center', padding: '12px 0' }}>
              Loading vitals…
            </p>
          ) : (
            <>
              {/* Readings list */}
              {readings.length === 0 ? (
                <p style={{ fontSize: 13, color: COLORS.textMuted, margin: '0 0 12px' }}>
                  No vitals recorded for this shift yet.
                </p>
              ) : (
                <div style={{ marginBottom: 12 }}>
                  {readings.map(r => (
                    <ReadingRow
                      key={r.adl_vitals_id}
                      reading={r}
                      isSigned={isSigned}
                      onEdit={!isSigned && !isLocked ? startEdit : null}
                    />
                  ))}
                </div>
              )}

              {/* Add / Amend reading form */}
              {showForm && (
                <div style={{
                  background: COLORS.bg,
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: 10, padding: '14px',
                  marginBottom: 12,
                }}>
                  <p style={{ fontSize: 13, fontWeight: 700, color: COLORS.text, margin: '0 0 12px' }}>
                    {editingId ? 'Edit Reading' : isSigned ? '+ Add Amendment' : '+ New Reading'}
                  </p>

                  {/* Time of reading */}
                  <div style={{ marginBottom: 12 }}>
                    <label htmlFor="vitals-recorded-at" style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMuted, display: 'block', marginBottom: 4 }}>
                      Time of Reading
                    </label>
                    <input
                      id="vitals-recorded-at"
                      type="datetime-local"
                      value={form.recorded_at}
                      onChange={e => handleFieldChange('recorded_at', e.target.value)}
                      disabled={saving}
                      style={{
                        padding: '7px 10px', borderRadius: 6,
                        border: `1px solid ${COLORS.border}`, fontSize: 13,
                        color: COLORS.text, background: COLORS.surface,
                      }}
                    />
                  </div>

                  {/* BP row */}
                  <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
                    <div style={{ flex: 1 }}>
                      <VitalInput name="systolic"  value={form.systolic}  onChange={handleFieldChange} disabled={saving} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <VitalInput name="diastolic" value={form.diastolic} onChange={handleFieldChange} disabled={saving} />
                    </div>
                  </div>

                  {/* Other vitals grid */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
                    <VitalInput name="pulse"            value={form.pulse}            onChange={handleFieldChange} disabled={saving} />
                    <VitalInput name="temperature"      value={form.temperature}      onChange={handleFieldChange} disabled={saving} />
                    <VitalInput name="o2_saturation"    value={form.o2_saturation}    onChange={handleFieldChange} disabled={saving} />
                    <VitalInput name="respiratory_rate" value={form.respiratory_rate} onChange={handleFieldChange} disabled={saving} />
                  </div>

                  {/* Notes */}
                  <div style={{ marginBottom: 12 }}>
                    <label htmlFor="vitals-notes" style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMuted, display: 'block', marginBottom: 4 }}>
                      Notes (optional)
                    </label>
                    <input
                      id="vitals-notes"
                      type="text"
                      value={form.notes}
                      onChange={e => handleFieldChange('notes', e.target.value)}
                      disabled={saving}
                      placeholder="Optional notes…"
                      style={{
                        width: '100%', padding: '7px 10px', borderRadius: 6,
                        border: `1px solid ${COLORS.border}`, fontSize: 13,
                        color: COLORS.text, background: COLORS.surface, boxSizing: 'border-box',
                      }}
                    />
                  </div>

                  {/* Form actions */}
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button
                      onClick={cancelForm}
                      disabled={saving}
                      style={{
                        padding: '8px 16px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
                        background: 'none', border: `1px solid ${COLORS.border}`, color: COLORS.textMuted,
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSave}
                      disabled={saving || !formHasValues()}
                      style={{
                        padding: '8px 20px', borderRadius: 6, fontSize: 12, fontWeight: 700,
                        cursor: saving || !formHasValues() ? 'not-allowed' : 'pointer',
                        background: COLORS.primary, color: '#fff', border: 'none',
                        opacity: saving || !formHasValues() ? 0.6 : 1,
                      }}
                    >
                      {saving ? 'Saving…' : editingId ? 'Update' : 'Save Reading'}
                    </button>
                  </div>
                </div>
              )}

              {/* Add reading button */}
              {!showForm && (isSigned ? !isCosigned : !isLocked) && (
                <button
                  onClick={startAdd}
                  style={{
                    width: '100%', padding: '9px', borderRadius: 8,
                    background: 'none', border: `1px dashed ${COLORS.border}`,
                    fontSize: 12, fontWeight: 600, color: COLORS.primary, cursor: 'pointer',
                  }}
                >
                  + {isSigned ? 'Add Amendment' : 'Add Reading'}
                </button>
              )}

              {/* Abnormal pre-sign confirmation */}
              {showConfirm && (
                <div style={{
                  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  zIndex: 1000,
                }}>
                  <div style={{
                    background: COLORS.surface, borderRadius: 12, padding: 24,
                    maxWidth: 380, width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                  }}>
                    <h3 style={{ margin: '0 0 8px', fontSize: 16, color: COLORS.text }}>
                      Sign Vitals Section
                    </h3>
                    <div style={{
                      background: COLORS.warningLight, border: `1px solid ${COLORS.warning}`,
                      borderRadius: 8, padding: '10px 12px', marginBottom: 16,
                    }}>
                      <p style={{ margin: 0, fontSize: 13, color: COLORS.warning, fontWeight: 600 }}>
                        ⚠ {abnormalReadings.length} reading(s) contain out-of-range values.
                      </p>
                      <p style={{ margin: '4px 0 0', fontSize: 12, color: COLORS.warning }}>
                        Please confirm you have notified or will notify the supervisor before signing.
                      </p>
                    </div>
                    <p style={{ fontSize: 13, color: COLORS.textMuted, margin: '0 0 16px' }}>
                      Once signed, existing readings cannot be edited. You may add amendment readings after signing.
                    </p>
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                      <button
                        onClick={() => setShowConfirm(false)}
                        disabled={signing}
                        style={{
                          padding: '9px 16px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
                          background: 'none', border: `1px solid ${COLORS.border}`, color: COLORS.textMuted,
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={doSign}
                        disabled={signing}
                        style={{
                          padding: '9px 20px', borderRadius: 6, fontSize: 12, fontWeight: 700,
                          cursor: signing ? 'not-allowed' : 'pointer',
                          background: COLORS.primary, color: '#fff', border: 'none',
                        }}
                      >
                        {signing ? 'Signing…' : 'Confirm & Sign'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}
    </ADLSectionCard>
  );
}